// @ts-nocheck
import React, { useState } from 'react';
import { Badge, Tooltip, Modal, Card, Row, Col, Statistic, Timeline, Button } from 'antd';
import { 
  CheckCircleOutlined, 
  ExclamationCircleOutlined, 
  CloseCircleOutlined, 
  InfoCircleOutlined,
  ReloadOutlined,
  HistoryOutlined 
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { colors, spacing } from '../design-system/tokens';
import { StatusPill } from '../design-system/StatusPill';
import { queryConfigs, queryKeys } from '../../utils/queryConfig';

interface IntegrationStatus {
  name: string;
  status: 'healthy' | 'degraded' | 'failed';
  lastCheckAt: string;
  lastSuccessAt: string | null;
  responseTime?: number;
  error?: string;
  details?: Record<string, any>;
}

interface HealthCheckResult {
  integrations: IntegrationStatus[];
  overall: 'healthy' | 'degraded' | 'failed';
  lastUpdated: string;
}

interface UptimeData {
  integration: string;
  period: string;
  uptime: number;
  incidents: number;
  avgResponseTime: number;
  data: Array<{
    timestamp: string;
    status: string;
    responseTime: number | null;
  }>;
}

// API client functions
const fetchHealthStatus = async (): Promise<HealthCheckResult> => {
  const token = localStorage.getItem('atlas_token');
  if (!token) throw new Error('No authentication token');
  
  const response = await fetch('/api/v1/health/', {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch health status');
  }
  
  return response.json();
};

const fetchUptimeData = async (integration: string, period: string = '24h'): Promise<UptimeData> => {
  const token = localStorage.getItem('atlas_token');
  if (!token) throw new Error('No authentication token');
  
  const response = await fetch(`/api/v1/health/uptime/${integration}?period=${period}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch uptime data');
  }
  
  return response.json();
};

interface HealthStatusStripProps {
  className?: string;
  style?: React.CSSProperties;
}

const HealthStatusStrip: React.FC<HealthStatusStripProps> = ({ className, style }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<string | null>(null);
  const [uptimePeriod, setUptimePeriod] = useState<string>('24h');

  // Fetch overall health status
  const { data: healthData = {} as HealthCheckResult, isLoading, refetch } = useQuery({
    queryKey: queryKeys.health.status(),
    queryFn: fetchHealthStatus,
    ...queryConfigs.criticalRealTime, // Health is critical real-time data
  });

  // Fetch uptime data for selected integration
  const { data: uptimeData = {} as UptimeData, isLoading: uptimeLoading } = useQuery({
    queryKey: queryKeys.health.uptime(selectedIntegration || '', uptimePeriod),
    queryFn: () => selectedIntegration ? fetchUptimeData(selectedIntegration, uptimePeriod) : null,
    enabled: !!selectedIntegration && modalVisible,
    ...queryConfigs.businessData, // Uptime data is less critical, use business data config
  });

  const getOverallStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return colors.status.positive;
      case 'degraded':
        return colors.status.warning;
      case 'failed':
        return colors.status.negative;
      default:
        return colors.text.secondary;
    }
  };

  const getOverallStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleOutlined />;
      case 'degraded':
        return <ExclamationCircleOutlined />;
      case 'failed':
        return <CloseCircleOutlined />;
      default:
        return <InfoCircleOutlined />;
    }
  };

  const getStatusVariant = (status: string): 'positive' | 'warning' | 'negative' | 'neutral' => {
    switch (status) {
      case 'healthy':
        return 'positive';
      case 'degraded':
        return 'warning';
      case 'failed':
        return 'negative';
      default:
        return 'neutral';
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const showIntegrationDetails = (integration: IntegrationStatus) => {
    setSelectedIntegration(integration.name.toLowerCase());
    setModalVisible(true);
  };

  if (isLoading || !healthData) {
    return (
      <div 
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: `${spacing.sm} ${spacing.lg}`,
          backgroundColor: colors.background.secondary,
          borderBottom: `1px solid ${colors.background.tertiary}`,
          fontSize: '12px',
          color: colors.text.secondary,
          ...style
        }}
      >
        <span>System Status: Loading...</span>
      </div>
    );
  }

  const healthyCount = (healthData as HealthCheckResult).integrations?.filter((i: IntegrationStatus) => i.status === 'healthy').length || 0;
  const totalCount = (healthData as HealthCheckResult).integrations?.length || 0;

  return (
    <>
      <div 
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: `${spacing.sm} ${spacing.lg}`,
          backgroundColor: colors.background.secondary,
          borderBottom: `1px solid ${colors.background.tertiary}`,
          fontSize: '12px',
          cursor: 'pointer',
          ...style
        }}
        onClick={() => setModalVisible(true)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
          <span style={{ color: getOverallStatusColor((healthData as HealthCheckResult).overall || 'healthy') }}>
            {getOverallStatusIcon((healthData as HealthCheckResult).overall || 'healthy')}
          </span>
          <span style={{ color: colors.text.primary, fontWeight: 500 }}>
            System Status: {(healthData as HealthCheckResult).overall ? (healthData as HealthCheckResult).overall.charAt(0).toUpperCase() + (healthData as HealthCheckResult).overall.slice(1) : 'Unknown'}
          </span>
          <span style={{ color: colors.text.secondary }}>
            ({healthyCount}/{totalCount} integrations healthy)
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.lg }}>
          <div style={{ display: 'flex', gap: spacing.xs }}>
            {(healthData as HealthCheckResult).integrations?.map((integration: IntegrationStatus) => (
              <Tooltip 
                key={integration.name} 
                title={`${integration.name}: ${integration.status}${integration.responseTime ? ` (${integration.responseTime}ms)` : ''}`}
              >
                <Badge 
                  status={integration.status === 'healthy' ? 'success' : integration.status === 'degraded' ? 'warning' : 'error'} 
                />
              </Tooltip>
            ))}
          </div>
          <span style={{ color: colors.text.tertiary }}>
            Updated {formatDateTime((healthData as HealthCheckResult).lastUpdated || new Date().toISOString())}
          </span>
        </div>
      </div>

      {/* Detailed Health Modal */}
      <Modal
        title="System Health Status"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedIntegration(null);
        }}
        footer={[
          <Button key="refresh" icon={<ReloadOutlined />} onClick={() => refetch()}>
            Refresh
          </Button>,
          <Button key="close" onClick={() => setModalVisible(false)}>
            Close
          </Button>
        ]}
        width={1000}
      >
        <div style={{ marginBottom: spacing.lg }}>
          {/* Overall Status */}
          <Card size="small" style={{ marginBottom: spacing.lg }}>
            <Row gutter={[24, 16]}>
              <Col span={6}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    fontSize: '24px', 
                    color: getOverallStatusColor(healthData.overall),
                    marginBottom: spacing.xs
                  }}>
                    {getOverallStatusIcon(healthData.overall)}
                  </div>
                  <div style={{ fontWeight: 600, fontSize: '16px', color: colors.text.primary }}>
                    {healthData.overall ? healthData.overall.charAt(0).toUpperCase() + healthData.overall.slice(1) : 'Unknown'}
                  </div>
                  <div style={{ fontSize: '12px', color: colors.text.secondary }}>
                    Overall Status
                  </div>
                </div>
              </Col>
              <Col span={6}>
                <Statistic
                  title="Healthy Services"
                  value={healthyCount}
                  suffix={`/ ${totalCount}`}
                  valueStyle={{ color: colors.status.positive }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Response Time"
                  value={healthData.integrations
                    ?.filter((i: IntegrationStatus) => i.responseTime)
                    .reduce((acc: number, i: IntegrationStatus) => acc + (i.responseTime || 0), 0) / 
                    (healthData.integrations?.filter((i: IntegrationStatus) => i.responseTime).length || 1) || 0
                  }
                  suffix="ms"
                  precision={0}
                  valueStyle={{ color: colors.text.primary }}
                />
              </Col>
              <Col span={6}>
                <div style={{ fontSize: '12px', color: colors.text.secondary }}>
                  Last Updated<br />
                  <span style={{ color: colors.text.primary, fontWeight: 500 }}>
                    {formatDateTime(healthData.lastUpdated)}
                  </span>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Integration Status Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: spacing.lg }}>
            {(healthData as HealthCheckResult).integrations?.map((integration: IntegrationStatus) => (
              <Col key={integration.name} xs={24} sm={12} lg={6}>
                <Card 
                  size="small" 
                  hoverable
                  onClick={() => showIntegrationDetails(integration)}
                  style={{ 
                    borderColor: integration.status === 'healthy' ? colors.status.positive : 
                                 integration.status === 'degraded' ? colors.status.warning : colors.status.negative
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: 600, marginBottom: spacing.xs, color: colors.text.primary }}>
                      {integration.name}
                    </div>
                    <StatusPill variant={getStatusVariant(integration.status)}>
                      {integration.status.charAt(0).toUpperCase() + integration.status.slice(1)}
                    </StatusPill>
                    
                    {integration.responseTime && (
                      <div style={{ fontSize: '12px', color: colors.text.secondary, marginTop: spacing.xs }}>
                        {integration.responseTime}ms
                      </div>
                    )}
                    
                    {integration.error && (
                      <div style={{ 
                        fontSize: '11px', 
                        color: colors.status.negative, 
                        marginTop: spacing.xs,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {integration.error}
                      </div>
                    )}
                    
                    <div style={{ fontSize: '10px', color: colors.text.tertiary, marginTop: spacing.xs }}>
                      Checked {formatDateTime(integration.lastCheckAt)}
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Uptime Details for Selected Integration */}
          {selectedIntegration && uptimeData && (
            <Card 
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                  <HistoryOutlined />
                  <span>{(uptimeData as UptimeData).integration} Uptime History</span>
                </div>
              }
              size="small"
            >
              <Row gutter={[24, 16]} style={{ marginBottom: spacing.lg }}>
                <Col span={8}>
                  <Statistic
                    title="Uptime"
                    value={(uptimeData as UptimeData).uptime}
                    precision={2}
                    suffix="%"
                    valueStyle={{ color: (uptimeData as UptimeData).uptime > 99 ? colors.status.positive : colors.status.warning }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Incidents"
                    value={(uptimeData as UptimeData).incidents}
                    valueStyle={{ color: (uptimeData as UptimeData).incidents === 0 ? colors.status.positive : colors.status.negative }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Avg Response"
                    value={(uptimeData as UptimeData).avgResponseTime}
                    suffix="ms"
                    valueStyle={{ color: colors.text.primary }}
                  />
                </Col>
              </Row>

              <Timeline
                items={(uptimeData as UptimeData).data.slice(-10).reverse().map((event, index) => ({
                  children: (
                    <div>
                      <div style={{ fontWeight: 600, color: colors.text.primary }}>
                        {event.status === 'healthy' ? 'Service Available' : 'Service Unavailable'}
                      </div>
                      <div style={{ fontSize: '12px', color: colors.text.secondary }}>
                        {formatDateTime(event.timestamp)}
                        {event.responseTime && ` • ${event.responseTime}ms`}
                      </div>
                    </div>
                  ),
                  color: event.status === 'healthy' ? colors.status.positive : colors.status.negative
                }))}
              />
            </Card>
          )}
        </div>
      </Modal>
    </>
  );
};

export default HealthStatusStrip;